/**
 * /find-my-peptide — "Find the Right Peptide" Assessment Quiz.
 *
 * Compliance-conscious lead-gen tool. The quiz does NOT recommend a specific
 * peptide — that would be practicing medicine. Instead it:
 *
 *   1. Asks about goals and general health areas
 *   2. Maps the responses to relevant TREATMENT CATEGORIES (e.g. Recovery,
 *      Longevity, Weight Management, etc.) — not specific peptides
 *   3. Surfaces relevant CMS articles + condition pages
 *   4. Ends with a Schedule Consultation CTA
 *
 * The end-user message is unambiguous:
 *   "Based on your answers, these are the areas a licensed medical provider
 *    could evaluate with you during a consultation. The specific therapy — if
 *    any — is determined by the provider after a full assessment."
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { ScheduleConsultationCTA } from "../components/ScheduleConsultationCTA";
import SEO from "../components/SEO";
import { trackEvent } from "../lib/analytics";

const QUESTIONS = [
  {
    id: "goals",
    title: "What are your primary health goals right now?",
    subtitle: "Select any that apply.",
    multi: true,
    options: [
      { id: "weight",     label: "Weight and metabolic health" },
      { id: "recovery",   label: "Recovery and athletic performance" },
      { id: "hormones",   label: "Hormone-related symptoms" },
      { id: "energy",     label: "Energy and fatigue" },
      { id: "cognitive",  label: "Cognitive support and focus" },
      { id: "aesthetic",  label: "Skin, hair, or aesthetic goals" },
      { id: "longevity",  label: "Longevity and healthy aging" },
      { id: "libido",     label: "Sexual health and libido" },
    ],
  },
  {
    id: "duration",
    title: "How long have you been managing these concerns?",
    multi: false,
    options: [
      { id: "under-3-months",   label: "Less than 3 months" },
      { id: "3-12-months",      label: "3 to 12 months" },
      { id: "1-3-years",        label: "1 to 3 years" },
      { id: "over-3-years",     label: "More than 3 years" },
    ],
  },
  {
    id: "recent_labs",
    title: "Have you had recent labs (within the past 12 months)?",
    multi: false,
    options: [
      { id: "yes-comprehensive", label: "Yes, including hormone or metabolic panels" },
      { id: "yes-basic",         label: "Yes, basic labs only" },
      { id: "no",                label: "No, or unsure" },
    ],
  },
  {
    id: "visit_pref",
    title: "How would you prefer to meet with a provider?",
    subtitle: "Availability depends on your state and clinical scenario.",
    multi: false,
    options: [
      { id: "in-person",   label: "In-person clinic visit" },
      { id: "telemedicine", label: "Telemedicine visit (where legally appropriate)" },
      { id: "either",       label: "Either, whatever the provider recommends" },
    ],
  },
];

// Map goal chips to treatment categories + relevant condition/article slugs
const RECOMMENDATIONS = {
  weight:    { category: "Weight Management",       goalSlug: "medical-weight-management",  articles: ["semaglutide-explained","tirzepatide-explained","understanding-glp-1-medications"], conditions: ["insulin-resistance"] },
  recovery:  { category: "Recovery",                goalSlug: "recovery-performance",       articles: ["ghk-cu-explained","how-peptides-work-in-the-body"],                                    conditions: [] },
  hormones:  { category: "Hormone Optimization",    goalSlug: "andropause",                 articles: ["science-behind-growth-hormone-releasing-peptides"],                                     conditions: ["low-testosterone","andropause","menopause","hypothyroidism"] },
  energy:    { category: "Longevity",               goalSlug: "longevity",                  articles: ["nad-plus-explained"],                                                                    conditions: ["hypothyroidism","insulin-resistance"] },
  cognitive: { category: "Cognitive Health",        goalSlug: "longevity",                  articles: ["nad-plus-explained"],                                                                    conditions: [] },
  aesthetic: { category: "Hair & Skin",             goalSlug: "aesthetic-skin",             articles: ["ghk-cu-explained"],                                                                       conditions: [] },
  longevity: { category: "Healthy Aging",           goalSlug: "longevity",                  articles: ["nad-plus-explained","how-peptides-work-in-the-body"],                                    conditions: [] },
  libido:    { category: "Hormone Optimization",    goalSlug: "andropause",                 articles: [],                                                                                          conditions: ["low-testosterone","andropause","menopause"] },
};

function uniq(arr) { return [...new Set(arr)]; }

export default function FindMyPeptide() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= QUESTIONS.length;
  const q = QUESTIONS[step];

  const setAnswer = (val) => {
    const current = answers[q.id] || (q.multi ? [] : "");
    let next;
    if (q.multi) {
      next = current.includes(val) ? current.filter((v) => v !== val) : [...current, val];
    } else {
      next = val;
    }
    setAnswers({ ...answers, [q.id]: next });
  };

  const advance = () => {
    if (step + 1 >= QUESTIONS.length) {
      try { trackEvent("assessment_completed", { answers }); } catch (_) {}
    }
    setStep(step + 1);
  };
  const goBack = () => setStep(Math.max(0, step - 1));
  const canAdvance = q ? (q.multi
    ? (answers[q.id] || []).length > 0
    : !!answers[q.id]
  ) : true;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-12" data-testid="find-my-peptide">
      <SEO
        title="Find the Right Peptide Therapy — Assessment | Medical Peptides"
        description="Take a quick assessment to see which treatment categories a licensed medical provider could evaluate with you. Results are educational; final therapy decisions require provider evaluation."
        canonical="https://medicalpeptides.ai/find-my-peptide"
      />

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--vv-teal)] font-semibold">
          <Sparkles size={13} /> Find the Right Peptide Assessment
        </div>
        <h1 className="font-display text-3xl md:text-4xl mt-2">A short questionnaire — not a prescription.</h1>
        <p className="text-sm text-stone-600 mt-2">
          Results highlight the treatment categories a licensed medical
          provider could evaluate with you. The specific therapy — if any — is
          determined by the provider after a full assessment.
        </p>
        <div className="mt-6 h-2 rounded-full bg-stone-100 overflow-hidden">
          <div
            className="h-full bg-[color:var(--vv-teal)] transition-all"
            style={{ width: `${(Math.min(step, QUESTIONS.length) / QUESTIONS.length) * 100}%` }}
          />
        </div>
      </div>

      {done ? (
        <ResultsScreen answers={answers} onRestart={() => { setStep(0); setAnswers({}); }} />
      ) : (
        <div data-testid={`quiz-step-${step + 1}`} className="rounded-3xl border border-stone-200 bg-white p-6 md:p-10">
          <h2 className="font-display text-2xl md:text-3xl mb-2">{q.title}</h2>
          {q.subtitle ? <p className="text-stone-600 text-sm mb-6">{q.subtitle}</p> : null}
          <div className="grid gap-2.5">
            {q.options.map((opt) => {
              const selected = q.multi
                ? (answers[q.id] || []).includes(opt.id)
                : answers[q.id] === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setAnswer(opt.id)}
                  data-testid={`quiz-opt-${q.id}-${opt.id}`}
                  className={[
                    "flex items-center gap-3 p-4 rounded-2xl text-left border transition-colors",
                    selected
                      ? "border-[color:var(--vv-teal)] bg-[color:var(--vv-teal)]/5"
                      : "border-stone-200 hover:border-stone-400",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "w-5 h-5 rounded-full border flex-shrink-0 flex items-center justify-center",
                      selected
                        ? "bg-[color:var(--vv-teal)] border-[color:var(--vv-teal)] text-white"
                        : "border-stone-300",
                    ].join(" ")}
                  >
                    {selected ? <CheckCircle2 size={14} /> : null}
                  </span>
                  <span className="text-stone-800">{opt.label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-8 flex items-center justify-between">
            <button
              type="button"
              onClick={goBack}
              disabled={step === 0}
              className="text-sm text-stone-500 hover:text-stone-800 flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="quiz-back"
            >
              <ArrowLeft size={16} /> Back
            </button>
            <button
              type="button"
              onClick={advance}
              disabled={!canAdvance}
              className={[
                "inline-flex items-center gap-2 rounded-full font-semibold px-5 py-2.5 text-sm transition-colors",
                canAdvance
                  ? "bg-[color:var(--vv-teal)] text-white hover:bg-[color:var(--vv-teal-hover)]"
                  : "bg-stone-200 text-stone-400 cursor-not-allowed",
              ].join(" ")}
              data-testid="quiz-next"
            >
              {step + 1 === QUESTIONS.length ? "See Results" : "Next"}
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      )}

      <p className="text-xs text-stone-500 text-center mt-10 max-w-2xl mx-auto">
        This assessment is educational. It is not medical advice, not a
        diagnosis, and not a prescription. All treatment decisions require
        evaluation by a licensed medical provider.
      </p>
    </div>
  );
}

function ResultsScreen({ answers, onRestart }) {
  const goals = answers.goals || [];
  const recs = uniq(goals.map((g) => RECOMMENDATIONS[g]).filter(Boolean).map((r) => r.category));
  const conditions = uniq(goals.flatMap((g) => RECOMMENDATIONS[g]?.conditions || []));
  const articles = uniq(goals.flatMap((g) => RECOMMENDATIONS[g]?.articles || []));
  const visitLabel = ({
    "in-person": "in-person clinic visits",
    "telemedicine": "telemedicine visits where legally appropriate",
    "either": "either in-person or telemedicine, depending on your provider",
  })[answers.visit_pref] || "";

  return (
    <div data-testid="quiz-results" className="rounded-3xl border border-stone-200 bg-white p-6 md:p-10">
      <div className="text-[11px] uppercase tracking-widest text-[color:var(--vv-teal)] font-semibold mb-2 flex items-center gap-2">
        <Sparkles size={13} /> Your assessment
      </div>
      <h2 className="font-display text-3xl md:text-4xl">Here's what a provider might evaluate with you.</h2>
      <p className="text-stone-700 mt-3 leading-relaxed">
        Based on your responses, a licensed medical provider could evaluate
        these treatment categories with you during a consultation. The
        specific therapy — if any — is determined by the provider after a full
        assessment. {visitLabel ? `You indicated a preference for ${visitLabel}.` : ""}
      </p>

      {recs.length > 0 ? (
        <div className="mt-8">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-3">
            Treatment categories worth exploring
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {recs.map((r) => (
              <div key={r} className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                <div className="font-semibold text-stone-900">{r}</div>
                <div className="text-xs text-stone-500 mt-1">Provider evaluation required</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {conditions.length > 0 ? (
        <div className="mt-8">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-3">
            Related conditions you might read about
          </div>
          <div className="flex flex-wrap gap-2">
            {conditions.map((c) => (
              <Link key={c} to={`/conditions/${c}`} className="rounded-full border border-stone-200 bg-white text-sm px-3 py-1.5 hover:border-[color:var(--vv-teal)] transition-colors">
                {c.replace(/-/g, " ")}
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      {articles.length > 0 ? (
        <div className="mt-8">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-3">
            Suggested educational reading
          </div>
          <div className="grid gap-2">
            {articles.map((a) => (
              <Link key={a} to={`/articles/${a}`} className="rounded-xl border border-stone-200 bg-white p-3 hover:border-[color:var(--vv-teal)] transition-colors flex items-center justify-between">
                <span className="text-sm text-stone-800">{a.replace(/-/g, " ")}</span>
                <ArrowRight size={14} className="text-stone-400" />
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-10 rounded-2xl bg-[color:var(--vv-ink)] text-white p-6 md:p-8">
        <h3 className="font-display text-xl md:text-2xl">Next step: meet with a provider</h3>
        <p className="text-stone-300 mt-2 text-sm leading-relaxed">
          Ready to see whether peptide therapy is appropriate for your goals?
          Book a consultation and share your assessment responses with your
          provider.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <ScheduleConsultationCTA location="assessment-results" variant="gold" size="lg" />
          <button type="button" onClick={onRestart} className="text-sm text-white/70 hover:text-white underline" data-testid="quiz-restart">Retake assessment</button>
        </div>
      </div>
    </div>
  );
}
